import { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { mockUser, mockDashboardData, mockApps } from '../../mocks';

export interface MockApiOptions {
  delay?: number;
  errorRate?: number;
}

const DEFAULT_OPTIONS: MockApiOptions = {
  delay: 500,
  errorRate: 0,
};

export class MockApiService {
  private options: MockApiOptions;

  constructor(options: Partial<MockApiOptions> = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  private async simulateDelay(): Promise<void> {
    if (this.options.delay && this.options.delay > 0) {
      await new Promise((resolve) => setTimeout(resolve, this.options.delay));
    }
  }

  private simulateError(): void {
    if (this.options.errorRate && Math.random() < this.options.errorRate) {
      throw {
        response: {
          status: 500,
          data: { message: 'Simulated server error' },
        },
      };
    }
  }

  private createResponse<T>(data: T): AxiosResponse<T> {
    return {
      data,
      status: 200,
      statusText: 'OK',
      headers: {},
      config: {} as InternalAxiosRequestConfig,
    };
  }

  public async handleRequest(config: InternalAxiosRequestConfig): Promise<AxiosResponse | null> {
    await this.simulateDelay();
    this.simulateError();

    const { url, method, data } = config;
    const path = url?.replace('/api', '') || '';

    // User Endpoints
    if (path === '/user/profile' && method?.toLowerCase() === 'get') {
      return this.createResponse(mockUser);
    }
    
    if (path === '/settings' && method?.toLowerCase() === 'post') {
      const parsedData = JSON.parse(data);
      mockUser.settings = { ...mockUser.settings, ...parsedData };
      return this.createResponse({ success: true, user: mockUser });
    }

    // Dashboard Endpoints
    if (path === '/dashboard/metrics' && method?.toLowerCase() === 'get') {
      return this.createResponse(mockDashboardData.metrics);
    }

    if (path === '/dashboard/activity' && method?.toLowerCase() === 'get') {
      return this.createResponse(mockDashboardData.recentActivity);
    }

    // Apps Endpoints
    if (path === '/apps' && method?.toLowerCase() === 'get') {
      return this.createResponse(mockApps);
    }

    // 404 for unknown endpoints
    return null;
  }
}

export const mockApi = new MockApiService();
